import { makeStyles } from '@material-ui/core/styles';

/**
 * Spacing is on the 2px grid, 16px default and 8px for the dense level rows —
 * the two are never mixed inside one region. Colours come from the FDS theme
 * palette; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  page: {
    padding: 24,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 16,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  search: {
    maxWidth: 320,
    flexGrow: 1,
  },
  list: {
    border: `1px solid ${theme.palette.common.gray200}`,
    borderRadius: 4,
  },
  rule: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  // A disabled rule stays listed — it is configuration the admin still owns, and
  // hiding it would make "why is this not firing?" unanswerable from this page.
  // Dimming pairs with the "Off" badge: opacity alone would be colour-as-state.
  ruleDisabled: {
    opacity: 0.5,
  },
  ruleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  ruleName: {
    marginRight: 8,
  },
  summary: {
    marginTop: 4,
  },
  // Dense region: the nested level rows sit at 8px, consistently, and never
  // borrow the 16px used by the rule rows around them.
  levels: {
    marginTop: 8,
    paddingLeft: 16,
    borderLeft: `2px solid ${theme.palette.common.gray200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  levelDisabled: {
    opacity: 0.45,
  },
  managedHint: {
    marginTop: 4,
  },
  empty: {
    padding: 48,
    textAlign: 'center',
  },
  emptyBody: {
    marginTop: 8,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    marginBottom: 16,
    border: `1px solid ${theme.palette.common.red400}`,
    background: theme.palette.common.red100,
    borderRadius: 4,
  },
  skeletonRow: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
  },
  // 640px per the design. Capped at the viewport so the panel does not overflow
  // a narrow window — a drawer wider than the screen puts its own actions off
  // the right edge.
  panel: {
    width: 640,
    maxWidth: '100vw',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    marginTop: 8,
  },
  // Warns that the level lists approvers the save will drop. Coloured *and*
  // worded — the sentence carries the meaning on its own, so the colour is
  // reinforcement rather than the signal.
  approverWarning: {
    marginTop: 4,
    color: theme.palette.warning.main,
  },
  levelCard: {
    border: `1px solid ${theme.palette.common.gray200}`,
    borderRadius: 4,
    padding: 16,
  },
  levelCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  panelNote: {
    marginTop: 8,
  },
  panelActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
    marginTop: 16,
  },
  editButton: {
    marginLeft: 'auto',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
  dialogWarning: {
    marginTop: 8,
    color: theme.palette.common.orange600,
  },
  dialogError: {
    marginTop: 16,
    color: theme.palette.common.red600,
  },
  // Destructive actions never carry primary weight — text, in the error role,
  // sitting after Cancel rather than replacing it as the filled button.
  dangerButton: {
    color: theme.palette.common.red600,
  },
  conditions: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  conditionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 8,
    borderLeft: `2px solid ${theme.palette.common.gray200}`,
  },
  // Holds the builder's place while the field catalog loads, so the section does
  // not read as "this level has no conditions" for the length of the request.
  conditionsPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
  },
}));
